import { Injectable } from '@angular/core';
import { v4 as generateGuid } from 'uuid';

const updateSessionTimeInterval = 300000;
const sessionDataKey = 'session-data';

interface StorageValue {
  sessionId: string;
  sessionStartTime: number;
}

@Injectable({
  providedIn: 'root',
})
export class SessionKeyProvider {
  constructor() {
    this.currentSessionId = this.getOrCreateCurrentSessionId();
    this.startSessionStartTimeUpdate();
  }

  readonly currentSessionId: string;

  /**
   * Generates new session id if there is no localstorage record or session expires.
   * Otherwise, returns current session id
   */
  private getOrCreateCurrentSessionId(): string {
    let currentStorageValue = this.getLocalStorageValue();

    if (
      !currentStorageValue ||
      new Date().getTime() - currentStorageValue.sessionStartTime > updateSessionTimeInterval
    ) {
      currentStorageValue = {
        sessionId: generateGuid(),
        sessionStartTime: new Date().getTime(),
      };
      this.setLocalStorageValue(currentStorageValue);
    }

    return currentStorageValue.sessionId;
  }

  private getLocalStorageValue(): StorageValue {
    try {
      const storageValueStr = localStorage.getItem(sessionDataKey);
      return storageValueStr ? JSON.parse(storageValueStr) : undefined;
    } catch {
      return undefined;
    }
  }

  private setLocalStorageValue(storageValue: StorageValue): void {
    localStorage.setItem(sessionDataKey, JSON.stringify(storageValue));
  }

  /**
   * Prolongs session in localstorage that allows current session id still be valid
   * until the user stops using the app in one tab or in multiple tabs
   * It means that if there is at least one active tab of the app
   * session will prolong
   */
  private startSessionStartTimeUpdate(): void {
    this.updateSessionStartTime();
    setInterval(() => this.updateSessionStartTime(), updateSessionTimeInterval);
  }

  /**
   * Prolongs session in localstorage that allows current session id still be valid
   */
  private updateSessionStartTime(): void {
    const currentStorageValue = this.getLocalStorageValue();

    if (!currentStorageValue) {
      return;
    }

    this.setLocalStorageValue({
      ...currentStorageValue,
      sessionStartTime: new Date().getTime(),
    });
  }
}
